/**
 * Local Party Mode Configuration
 *
 * Types, enums, and presets for local multiplayer party games.
 * Supports multiple game modes, team configurations, and customizable settings.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Game mode determines win conditions and gameplay style
 */
export enum LocalGameMode {
  /** Last worm standing, configurable lives */
  SURVIVAL = 'survival',
  /** First to reach target score wins */
  SCORE_RACE = 'score_race',
  /** Highest score when time runs out wins */
  TIME_ATTACK = 'time_attack',
  /** Hold the crown for cumulative time to win */
  KING_OF_WORMS = 'king'
}

/**
 * Team configuration for multiplayer matches
 */
export enum TeamMode {
  /** Every player for themselves */
  FREE_FOR_ALL = 'ffa',
  /** Two teams (red vs blue) */
  TEAMS = 'teams',
  /** Two-player teams (pairs) */
  DUOS = 'duos',
  /** Player-defined custom team assignments */
  CUSTOM = 'custom'
}

/**
 * Map boundary shape
 */
export enum MapShape {
  /** Circular arena */
  CIRCLE = 'circle',
  /** Rectangular arena */
  RECTANGLE = 'rectangle'
}

// ============================================================================
// TYPES
// ============================================================================

/** Shrink speed options */
export type ShrinkSpeed = 'slow' | 'normal' | 'fast' | 'none';

/** AI difficulty levels */
export type AIDifficulty = 'easy' | 'normal' | 'hard';

/** Food spawn density */
export type FoodDensity = 'sparse' | 'normal' | 'dense';

/** Powerup rate preset names */
export type PowerupRatePresetId = 'scarce' | 'rare' | 'normal' | 'abundant' | 'plentiful' | 'mayhem';

/** Visibility preset names */
export type VisibilityPresetId = 'full_vision' | 'eagle_eye' | 'normal' | 'limited' | 'dense_fog' | 'extreme_fog';

/**
 * Complete configuration for a Local Party game session
 */
export interface LocalPartyConfig {
  // Game Rules
  /** Primary game mode determining win conditions */
  gameMode: LocalGameMode;
  /** Team configuration */
  teamMode: TeamMode;

  // Map Settings
  /** Shape of the play area */
  mapShape: MapShape;
  /** Initial map size (radius for circle, half-width for rectangle) */
  mapSize: number;
  /** Whether the map shrinks over time */
  shrinkEnabled: boolean;
  /** How fast the map shrinks */
  shrinkSpeed: ShrinkSpeed;
  /** Seconds before shrinking begins */
  shrinkDelay: number;

  // Player Settings
  /** Maximum human players (1-16) */
  maxPlayers: number;
  /** Number of AI-controlled worms (0-20) */
  aiWorms: number;
  /** AI behavior difficulty */
  aiDifficulty: AIDifficulty;

  // Mode-Specific Settings
  /** Lives per player for Survival mode (1-10) */
  lives: number;
  /** Score needed to win in Score Race mode */
  targetScore: number;
  /** Duration in seconds for Time Attack mode */
  timeLimit: number;
  /** Seconds of crown possession needed to win King of Worms */
  crownTime: number;

  // Gameplay Options
  /** Whether teammates can damage each other */
  friendlyFire: boolean;
  /** If false, host is spectator only */
  hostPlays: boolean;
  /** Seconds before respawning after death */
  respawnDelay: number;
  /** Food spawn density on the map */
  foodDensity: FoodDensity;
  /** Powerup spawn rate preset */
  powerupRatePreset: PowerupRatePresetId;
  /** Visibility/fog of war preset */
  visibilityPreset: VisibilityPresetId;
}

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/** Validation limits for configuration values */
export const CONFIG_LIMITS = {
  maxPlayers: { min: 1, max: 16 },
  aiWorms: { min: 0, max: 20 },
  lives: { min: 1, max: 10 },
  targetScore: { min: 100, max: 100000 },
  timeLimit: { min: 30, max: 1800 },
  crownTime: { min: 10, max: 300 },
  mapSize: { min: 1000, max: 10000 },
  shrinkDelay: { min: 0, max: 300 },
  respawnDelay: { min: 0, max: 30 }
} as const;

// ============================================================================
// PRESETS
// ============================================================================

/**
 * Quick Survival preset
 * Fast-paced elimination with quick shrinking
 */
export const PRESET_QUICK_SURVIVAL: LocalPartyConfig = {
  gameMode: LocalGameMode.SURVIVAL,
  teamMode: TeamMode.FREE_FOR_ALL,
  mapShape: MapShape.CIRCLE,
  mapSize: 4000,
  shrinkEnabled: true,
  shrinkSpeed: 'fast',
  shrinkDelay: 30,
  maxPlayers: 8,
  aiWorms: 0,
  aiDifficulty: 'normal',
  lives: 3,
  targetScore: 1000,
  timeLimit: 180,
  crownTime: 60,
  friendlyFire: true,
  hostPlays: true,
  respawnDelay: 3,
  foodDensity: 'normal',
  powerupRatePreset: 'abundant',
  visibilityPreset: 'normal'
};

/**
 * Team Battle preset
 * Two teams compete with no friendly fire
 */
export const PRESET_TEAM_BATTLE: LocalPartyConfig = {
  gameMode: LocalGameMode.SURVIVAL,
  teamMode: TeamMode.TEAMS,
  mapShape: MapShape.CIRCLE,
  mapSize: 5000,
  shrinkEnabled: true,
  shrinkSpeed: 'normal',
  shrinkDelay: 60,
  maxPlayers: 8,
  aiWorms: 0,
  aiDifficulty: 'normal',
  lives: 3,
  targetScore: 1000,
  timeLimit: 300,
  crownTime: 60,
  friendlyFire: false,
  hostPlays: true,
  respawnDelay: 5,
  foodDensity: 'normal',
  powerupRatePreset: 'abundant',
  visibilityPreset: 'normal'
};

/**
 * Party Chaos preset
 * Maximum players, dense food, fast action
 */
export const PRESET_PARTY_CHAOS: LocalPartyConfig = {
  gameMode: LocalGameMode.TIME_ATTACK,
  teamMode: TeamMode.FREE_FOR_ALL,
  mapShape: MapShape.CIRCLE,
  mapSize: 6000,
  shrinkEnabled: true,
  shrinkSpeed: 'fast',
  shrinkDelay: 20,
  maxPlayers: 16,
  aiWorms: 4,
  aiDifficulty: 'easy',
  lives: 5,
  targetScore: 2000,
  timeLimit: 120,
  crownTime: 45,
  friendlyFire: true,
  hostPlays: true,
  respawnDelay: 2,
  foodDensity: 'dense',
  powerupRatePreset: 'mayhem',
  visibilityPreset: 'normal'
};

/**
 * Practice preset
 * Relaxed mode for learning, host spectates
 */
export const PRESET_PRACTICE: LocalPartyConfig = {
  gameMode: LocalGameMode.SURVIVAL,
  teamMode: TeamMode.FREE_FOR_ALL,
  mapShape: MapShape.CIRCLE,
  mapSize: 5000,
  shrinkEnabled: false,
  shrinkSpeed: 'none',
  shrinkDelay: 0,
  maxPlayers: 4,
  aiWorms: 3,
  aiDifficulty: 'easy',
  lives: 10,
  targetScore: 1000,
  timeLimit: 600,
  crownTime: 120,
  friendlyFire: true,
  hostPlays: false,
  respawnDelay: 1,
  foodDensity: 'dense',
  powerupRatePreset: 'plentiful',
  visibilityPreset: 'eagle_eye'
};

/**
 * Competitive preset
 * Balanced settings for serious play
 */
export const PRESET_COMPETITIVE: LocalPartyConfig = {
  gameMode: LocalGameMode.SCORE_RACE,
  teamMode: TeamMode.FREE_FOR_ALL,
  mapShape: MapShape.CIRCLE,
  mapSize: 4500,
  shrinkEnabled: true,
  shrinkSpeed: 'slow',
  shrinkDelay: 120,
  maxPlayers: 8,
  aiWorms: 0,
  aiDifficulty: 'hard',
  lives: 1,
  targetScore: 5000,
  timeLimit: 600,
  crownTime: 90,
  friendlyFire: true,
  hostPlays: true,
  respawnDelay: 5,
  foodDensity: 'normal',
  powerupRatePreset: 'normal',
  visibilityPreset: 'normal'
};

/** Map of all available presets by name */
export const PRESETS: Record<string, LocalPartyConfig> = {
  'quick_survival': PRESET_QUICK_SURVIVAL,
  'team_battle': PRESET_TEAM_BATTLE,
  'party_chaos': PRESET_PARTY_CHAOS,
  'practice': PRESET_PRACTICE,
  'competitive': PRESET_COMPETITIVE
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Returns the default configuration for a new local party game
 * Uses Quick Survival as the base default
 */
export function getDefaultConfig(): LocalPartyConfig {
  return { ...PRESET_QUICK_SURVIVAL };
}

/**
 * Returns a configuration based on the specified preset name
 * Falls back to default config if preset not found
 *
 * @param preset - Name of the preset to apply
 * @returns A copy of the preset configuration
 */
export function applyPreset(preset: string): LocalPartyConfig {
  const presetConfig = PRESETS[preset.toLowerCase()];
  if (presetConfig) {
    return { ...presetConfig };
  }
  return getDefaultConfig();
}

/**
 * Validates a LocalPartyConfig and returns any errors found
 *
 * @param config - The configuration to validate
 * @returns Array of error messages (empty if valid)
 */
export function validateConfig(config: LocalPartyConfig): string[] {
  const errors: string[] = [];

  // Validate maxPlayers
  if (config.maxPlayers < CONFIG_LIMITS.maxPlayers.min ||
      config.maxPlayers > CONFIG_LIMITS.maxPlayers.max) {
    errors.push(
      `maxPlayers must be between ${CONFIG_LIMITS.maxPlayers.min} and ${CONFIG_LIMITS.maxPlayers.max}`
    );
  }

  // Validate aiWorms
  if (config.aiWorms < CONFIG_LIMITS.aiWorms.min ||
      config.aiWorms > CONFIG_LIMITS.aiWorms.max) {
    errors.push(
      `aiWorms must be between ${CONFIG_LIMITS.aiWorms.min} and ${CONFIG_LIMITS.aiWorms.max}`
    );
  }

  // Validate lives
  if (config.lives < CONFIG_LIMITS.lives.min ||
      config.lives > CONFIG_LIMITS.lives.max) {
    errors.push(
      `lives must be between ${CONFIG_LIMITS.lives.min} and ${CONFIG_LIMITS.lives.max}`
    );
  }

  // Validate targetScore
  if (config.targetScore < CONFIG_LIMITS.targetScore.min ||
      config.targetScore > CONFIG_LIMITS.targetScore.max) {
    errors.push(
      `targetScore must be between ${CONFIG_LIMITS.targetScore.min} and ${CONFIG_LIMITS.targetScore.max}`
    );
  }

  // Validate timeLimit
  if (config.timeLimit < CONFIG_LIMITS.timeLimit.min ||
      config.timeLimit > CONFIG_LIMITS.timeLimit.max) {
    errors.push(
      `timeLimit must be between ${CONFIG_LIMITS.timeLimit.min} and ${CONFIG_LIMITS.timeLimit.max} seconds`
    );
  }

  // Validate crownTime
  if (config.crownTime < CONFIG_LIMITS.crownTime.min ||
      config.crownTime > CONFIG_LIMITS.crownTime.max) {
    errors.push(
      `crownTime must be between ${CONFIG_LIMITS.crownTime.min} and ${CONFIG_LIMITS.crownTime.max} seconds`
    );
  }

  // Validate mapSize
  if (config.mapSize < CONFIG_LIMITS.mapSize.min ||
      config.mapSize > CONFIG_LIMITS.mapSize.max) {
    errors.push(
      `mapSize must be between ${CONFIG_LIMITS.mapSize.min} and ${CONFIG_LIMITS.mapSize.max}`
    );
  }

  // Validate shrinkDelay
  if (config.shrinkDelay < CONFIG_LIMITS.shrinkDelay.min ||
      config.shrinkDelay > CONFIG_LIMITS.shrinkDelay.max) {
    errors.push(
      `shrinkDelay must be between ${CONFIG_LIMITS.shrinkDelay.min} and ${CONFIG_LIMITS.shrinkDelay.max} seconds`
    );
  }

  // Validate respawnDelay
  if (config.respawnDelay < CONFIG_LIMITS.respawnDelay.min ||
      config.respawnDelay > CONFIG_LIMITS.respawnDelay.max) {
    errors.push(
      `respawnDelay must be between ${CONFIG_LIMITS.respawnDelay.min} and ${CONFIG_LIMITS.respawnDelay.max} seconds`
    );
  }

  // Validate enum values
  if (!Object.values(LocalGameMode).includes(config.gameMode)) {
    errors.push(`Invalid gameMode: ${config.gameMode}`);
  }

  if (!Object.values(TeamMode).includes(config.teamMode)) {
    errors.push(`Invalid teamMode: ${config.teamMode}`);
  }

  if (!Object.values(MapShape).includes(config.mapShape)) {
    errors.push(`Invalid mapShape: ${config.mapShape}`);
  }

  // Validate shrinkSpeed
  const validShrinkSpeeds: ShrinkSpeed[] = ['slow', 'normal', 'fast', 'none'];
  if (!validShrinkSpeeds.includes(config.shrinkSpeed)) {
    errors.push(`Invalid shrinkSpeed: ${config.shrinkSpeed}`);
  }

  // Validate aiDifficulty
  const validDifficulties: AIDifficulty[] = ['easy', 'normal', 'hard'];
  if (!validDifficulties.includes(config.aiDifficulty)) {
    errors.push(`Invalid aiDifficulty: ${config.aiDifficulty}`);
  }

  // Validate foodDensity
  const validDensities: FoodDensity[] = ['sparse', 'normal', 'dense'];
  if (!validDensities.includes(config.foodDensity)) {
    errors.push(`Invalid foodDensity: ${config.foodDensity}`);
  }

  // Logical validations
  if (config.shrinkEnabled && config.shrinkSpeed === 'none') {
    errors.push('shrinkSpeed cannot be "none" when shrinkEnabled is true');
  }

  // Team mode validations
  if (config.teamMode === TeamMode.TEAMS && config.maxPlayers < 2) {
    errors.push('Teams mode requires at least 2 players');
  }

  if (config.teamMode === TeamMode.DUOS && config.maxPlayers < 4) {
    errors.push('Duos mode requires at least 4 players');
  }

  // Ensure there's at least one participant
  if (config.maxPlayers === 0 && config.aiWorms === 0) {
    errors.push('Game must have at least one player or AI worm');
  }

  if (!config.hostPlays && config.maxPlayers <= 1 && config.aiWorms === 0) {
    errors.push('If host is spectating, there must be other players or AI worms');
  }

  return errors;
}

/**
 * Returns a list of available preset names
 */
export function getPresetNames(): string[] {
  return Object.keys(PRESETS);
}

/**
 * Merges partial config with defaults
 *
 * @param partial - Partial configuration to merge
 * @returns Complete configuration with defaults filled in
 */
export function mergeWithDefaults(partial: Partial<LocalPartyConfig>): LocalPartyConfig {
  return {
    ...getDefaultConfig(),
    ...partial
  };
}

/**
 * Gets display name for a game mode
 */
export function getGameModeDisplayName(mode: LocalGameMode): string {
  const names: Record<LocalGameMode, string> = {
    [LocalGameMode.SURVIVAL]: 'Survival',
    [LocalGameMode.SCORE_RACE]: 'Score Race',
    [LocalGameMode.TIME_ATTACK]: 'Time Attack',
    [LocalGameMode.KING_OF_WORMS]: 'King of Worms'
  };
  return names[mode] || mode;
}

/**
 * Gets display name for a team mode
 */
export function getTeamModeDisplayName(mode: TeamMode): string {
  const names: Record<TeamMode, string> = {
    [TeamMode.FREE_FOR_ALL]: 'Free For All',
    [TeamMode.TEAMS]: 'Teams',
    [TeamMode.DUOS]: 'Duos',
    [TeamMode.CUSTOM]: 'Custom Teams'
  };
  return names[mode] || mode;
}

/**
 * Gets description for a game mode
 */
export function getGameModeDescription(mode: LocalGameMode): string {
  const descriptions: Record<LocalGameMode, string> = {
    [LocalGameMode.SURVIVAL]: 'Last worm standing wins! Each player has limited lives.',
    [LocalGameMode.SCORE_RACE]: 'First player to reach the target score wins!',
    [LocalGameMode.TIME_ATTACK]: 'Get the highest score before time runs out!',
    [LocalGameMode.KING_OF_WORMS]: 'Grab the crown and hold it to accumulate winning time!'
  };
  return descriptions[mode] || '';
}

/**
 * Gets display name for powerup rate preset
 */
export function getPowerupRateDisplayName(preset: PowerupRatePresetId): string {
  const names: Record<PowerupRatePresetId, string> = {
    'scarce': 'Scarce',
    'rare': 'Rare',
    'normal': 'Normal',
    'abundant': 'Abundant',
    'plentiful': 'Plentiful',
    'mayhem': 'Mayhem'
  };
  return names[preset] || preset;
}

/**
 * Gets display name for visibility preset
 */
export function getVisibilityDisplayName(preset: VisibilityPresetId): string {
  const names: Record<VisibilityPresetId, string> = {
    'full_vision': 'Full Vision',
    'eagle_eye': 'Eagle Eye',
    'normal': 'Normal',
    'limited': 'Limited',
    'dense_fog': 'Dense Fog',
    'extreme_fog': 'Extreme Fog'
  };
  return names[preset] || preset;
}

/**
 * Gets display name for AI difficulty
 */
export function getAIDifficultyDisplayName(difficulty: AIDifficulty): string {
  const names: Record<AIDifficulty, string> = {
    'easy': 'Easy',
    'normal': 'Normal',
    'hard': 'Hard'
  };
  return names[difficulty] || difficulty;
}
